import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../../../context/CartContext";
import { useAuth } from "../../../context/AuthContext";
import { useAuthPrompt } from "../../../context/AuthPromptContext";
import { useToast } from "../../../context/ToastContext";
import { getBaskets, getProducts, getFarmReviews, getFarmReviewSummary, createReview, getFarmSubscriptions, subscribeToPlan, getFarmById } from "../../../Utils/storeService";
import { formatLek } from "../../../Utils/currency";
import {
  isLowAvailability,
  isSoldOut,
  readAvailability,
} from "../../../Utils/availability";
import MeetTheFarmer from "./MeetTheFarmer";
import "./FarmBaskets.css";

const FarmBaskets = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { openAuthPrompt } = useAuthPrompt();
  const { success: toastSuccess, error: toastError } = useToast();
  const [baskets, setBaskets] = useState([]);
  const [products, setProducts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [newReview, setNewReview] = useState({ rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [subPlans, setSubPlans] = useState([]);
  const [subscribingTo, setSubscribingTo] = useState(null);
  const [farmDetail, setFarmDetail] = useState(null);

  useEffect(() => {
    const loadBaskets = async () => {
      try {
        const data = await getBaskets(id ? Number(id) : undefined);
        const mapped = (data || []).map((basket) => ({
          ...basket,
          price: Number(basket.price || 0),
          image: basket.imageUrl || null,
        }));
        setBaskets(mapped);
      } catch (err) {
        console.error(err);
        setError(t("farmBaskets.errors.load", { defaultValue: "Failed to load baskets." }));
      } finally {
        setLoading(false);
      }
    };
    loadBaskets();
  }, [id, t]);

  // The farm page used to fetch baskets only, so a farmer who had published
  // products but not yet built a basket saw a completely empty storefront while
  // their portal listed those products as Active. Products are shown for
  // discovery; baskets remain the purchasable unit (Cart/Order key on BasketId).
  useEffect(() => {
    if (!id) return;
    let active = true;
    getProducts(Number(id))
      .then((data) => {
        if (!active) return;
        setProducts(
          (Array.isArray(data) ? data : [])
            .filter((product) => product.stock > 0)
            .map((product) => ({
              id: product.id,
              name: product.name,
              description: product.description,
              price: Number(product.price || 0),
              unit: product.unit,
              image: product.imageUrl || null,
            }))
        );
      })
      .catch(() => {
        // Non-fatal: baskets are the primary content, so a products failure
        // must not blank the page.
        if (active) setProducts([]);
      });
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const loadReviews = async () => {
      try {
        const [revs, summary] = await Promise.all([
          getFarmReviews(Number(id)),
          getFarmReviewSummary(Number(id)),
        ]);
        setReviews(revs || []);
        setReviewSummary(summary);
      } catch (err) {
        // Reviews are non-critical, don't block
      }
    };
    loadReviews();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    getFarmSubscriptions(Number(id))
      .then((data) => setSubPlans(data || []))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    getFarmById(Number(id))
      .then((data) => setFarmDetail(data))
      .catch(() => {});
  }, [id]);

  const performSubscribe = async (planId) => {
    setSubscribingTo(planId);
    try {
      await subscribeToPlan(token, planId);
      toastSuccess(t("farmBaskets.subscribe_success", { defaultValue: "Subscribed! View your subscriptions in your dashboard." }));
    } catch (err) {
      const msg = err?.response?.data;
      toastError(typeof msg === "string" ? msg : t("farmBaskets.errors.subscribe", { defaultValue: "Failed to subscribe." }));
    } finally {
      setSubscribingTo(null);
    }
  };

  const handleSubscribe = (planId) => {
    if (!token) {
      openAuthPrompt({
        title: t("auth_prompt.subscribe_title", { defaultValue: "Log in to subscribe" }),
        body: t("auth_prompt.subscribe_body", { defaultValue: "You need an account to subscribe to farm delivery plans." }),
        source: "subscribe",
        onSuccess: () => performSubscribe(planId),
      });
      return;
    }
    performSubscribe(planId);
  };

  const performSubmitReview = async () => {
    if (!newReview.comment.trim()) { toastError(t("farmBaskets.review_comment_required", { defaultValue: "Please write a comment." })); return; }
    setSubmittingReview(true);
    try {
      const created = await createReview(token, {
        farmId: Number(id),
        rating: newReview.rating,
        comment: newReview.comment.trim(),
      });
      setReviews((prev) => [created, ...prev]);
      setNewReview({ rating: 5, comment: "" });
      toastSuccess(t("farmBaskets.review_submitted", { defaultValue: "Review submitted!" }));
      const summary = await getFarmReviewSummary(Number(id));
      setReviewSummary(summary);
    } catch {
      toastError(t("farmBaskets.errors.review", { defaultValue: "Failed to submit review." }));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleSubmitReview = () => {
    if (!token) {
      openAuthPrompt({
        title: t("auth_prompt.review_title", { defaultValue: "Log in to leave a review" }),
        body: t("auth_prompt.review_body", { defaultValue: "Share your experience with this farm after logging in." }),
        source: "review",
        onSuccess: () => performSubmitReview(),
      });
      return;
    }
    performSubmitReview();
  };

  const [sortOption, setSortOption] = useState("default");
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [notification, setNotification] = useState("");

  const sortBaskets = (list) => {
    if (sortOption === "lowToHigh") {
      return [...list].sort((a, b) => {
        const priceA = a.price;
        const priceB = b.price;
        return priceA - priceB;
      });
    } else if (sortOption === "highToLow") {
      return [...list].sort((a, b) => {
        const priceA = a.price;
        const priceB = b.price;
        return priceB - priceA;
      });
    }
    return list;
  };

  const sortedBaskets = sortBaskets(baskets);

  const closeModal = () => setSelectedBasket(null);

  const { addItemToCart } = useCart();

  const handleAddToCart = (basket) => {
    const added = addItemToCart({
      ...basket,
      farmName: farmDetail?.name || "",
    });
    if (added) {
      setNotification(`${basket.name} ${t("farmBaskets.add_to_cart")}`);
      setTimeout(() => setNotification(""), 3000);
    }
  };

  return (
    <div className="farm-baskets-page">
      {/* Until this existed, opening a farm led to a page headed "Farm Baskets" with no farm
          name, banner or logo anywhere — the farm's own identity never appeared. */}
      {farmDetail ? (
        <header className="farm-hero">
          {farmDetail.bannerImageUrl ? (
            <div className="farm-hero-banner">
              <img src={farmDetail.bannerImageUrl} alt="" aria-hidden="true" />
              <div className="farm-hero-scrim" aria-hidden="true" />
            </div>
          ) : null}
          <div className="farm-hero-body">
            {farmDetail.logoImageUrl ? (
              <img
                className="farm-hero-logo"
                src={farmDetail.logoImageUrl}
                alt={farmDetail.name}
              />
            ) : null}
            <div className="farm-hero-text">
              <h1>{farmDetail.name}</h1>
              {farmDetail.location ? (
                <p className="farm-hero-location">{farmDetail.location}</p>
              ) : null}
              {farmDetail.description ? (
                <p className="farm-hero-description">{farmDetail.description}</p>
              ) : null}
            </div>
          </div>
        </header>
      ) : (
        <>
          <h1>{t("farmBaskets.page_title")}</h1>
          <p>{t("farmBaskets.page_subtitle")}</p>
        </>
      )}
      {farmDetail && <MeetTheFarmer farm={farmDetail} />}
      <div className="controls">
        <select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
        >
          <option value="default">{t("farmBaskets.sort_default")}</option>
          <option value="lowToHigh">{t("farmBaskets.sort_lowToHigh")}</option>
          <option value="highToLow">{t("farmBaskets.sort_highToLow")}</option>
        </select>
      </div>
      {notification && <div className="notification">{notification}</div>}
      {loading ? <p>{t("farmBaskets.loading", { defaultValue: "Loading baskets..." })}</p> : null}
      {error ? <p>{error}</p> : null}
      <div className="baskets-container">
        {sortedBaskets.map((basket) => {
          const available = readAvailability(basket);
          const soldOut = isSoldOut(available);
          return (
          <div
            key={basket.id}
            className="basket-card"
            onClick={() => navigate(`/baskets/${basket.id}`)}
          >
            <img
              src={basket.image}
              alt={basket.name}
              className="basket-image"
              loading="lazy"
            />
            <h2>{basket.name}</h2>
            <p>{basket.description}</p>
            <p className="price">{formatLek(basket.price)}</p>
            {soldOut ? (
              <span className="badge">{t("baskets.sold_out", { defaultValue: "Sold out" })}</span>
            ) : isLowAvailability(available) ? (
              <span className="badge low">
                {t("baskets.only_left", { count: available, defaultValue: "Only {{count}} left" })}
              </span>
            ) : null}
            {basket.isHot && <span className="badge hot">{t("farmBaskets.badge_hot", { defaultValue: "Hot!" })}</span>}
            {basket.isNew && <span className="badge new">{t("farmBaskets.badge_new", { defaultValue: "New!" })}</span>}
            <button
              className="add-to-cart-btn"
              disabled={soldOut}
              onClick={(e) => {
                e.stopPropagation();
                handleAddToCart(basket);
              }}
            >
              {soldOut
                ? t("baskets.sold_out", { defaultValue: "Sold out" })
                : t("farmBaskets.add_to_cart")}
            </button>
          </div>
          );
        })}
      </div>

      {/* A farm sells baskets. Its products are the ingredients behind them, so they
          stay folded away rather than competing with the baskets for attention. */}
      {products.length > 0 ? (
        <div className="reviews-wrapper" style={{ marginTop: 32 }} data-testid="farm-products">
          <h2>{t("farmBaskets.products_title", { defaultValue: "Fresh from this farm" })}</h2>
          <button
            type="button"
            className="farm-products-toggle"
            aria-expanded={showProducts}
            aria-controls="farm-products-panel"
            onClick={() => setShowProducts((open) => !open)}
          >
            {t("farmBaskets.products_toggle", {
              count: products.length,
              defaultValue: "See all products ({{count}})",
            })}
          </button>
          {showProducts ? (
            <div id="farm-products-panel">
              <p>
                {t("farmBaskets.products_hint", {
                  defaultValue: "These go into the farm's baskets.",
                })}
              </p>
              <div className="baskets-container">
                {products.map((product) => (
                  <div key={product.id} className="basket-card" style={{ cursor: "default" }}>
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="basket-image"
                        loading="lazy"
                      />
                    ) : null}
                    <h2>{product.name}</h2>
                    {product.description ? <p>{product.description}</p> : null}
                    <p className="price">
                      {product.unit
                        ? `${formatLek(product.price)} / ${product.unit}`
                        : formatLek(product.price)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Subscription Plans Section */}
      {subPlans.length > 0 && (
        <div className="reviews-wrapper" style={{ marginTop: 32 }}>
          <h2>{t("farmBaskets.subscriptions_title", { defaultValue: "Subscription Plans" })}</h2>
          <p style={{ color: "var(--color-text-secondary)", marginBottom: 16 }}>
            {t("farmBaskets.subscriptions_subtitle", { defaultValue: "Subscribe for regular deliveries from this farm" })}
          </p>
          <div className="baskets-container">
            {subPlans.map((plan) => (
              <div key={plan.id} className="basket-card" style={{ cursor: "default" }}>
                <h2 style={{ marginTop: 8 }}>{plan.name}</h2>
                {plan.description && <p>{plan.description}</p>}
                <p className="price">{`${formatLek(plan.price)} / ${plan.frequency}`}</p>
                <p style={{ fontSize: "0.85em", color: "var(--color-text-secondary)" }}>
                  {t("farmBaskets.subscribers", {
                    defaultValue: "{{active}}/{{max}} subscribers",
                    active: plan.activeSubscriberCount,
                    max: plan.maxSubscribers,
                  })}
                </p>
                {plan.boxItems?.length > 0 && (
                  <div style={{ fontSize: "0.85em", marginBottom: 8 }}>
                    <strong>{t("farmBaskets.box_includes", { defaultValue: "Box includes:" })}</strong>{" "}
                    {plan.boxItems.map((b) => `${b.productName} x${b.quantity}`).join(", ")}
                  </div>
                )}
                <button
                  className="add-to-cart-btn"
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={subscribingTo === plan.id || plan.activeSubscriberCount >= plan.maxSubscribers}
                >
                  {subscribingTo === plan.id
                    ? t("farmBaskets.subscribing", { defaultValue: "Subscribing..." })
                    : plan.activeSubscriberCount >= plan.maxSubscribers
                    ? t("farmBaskets.fully_subscribed", { defaultValue: "Fully Subscribed" })
                    : t("farmBaskets.subscribe", { defaultValue: "Subscribe" })}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews Section */}
      <div className="reviews-wrapper">
        <h2>
          {t("farmBaskets.reviews_title", { defaultValue: "Customer Reviews" })}
          {reviewSummary && reviewSummary.totalReviews > 0 && (
            <span className="reviews-summary">
              {"★".repeat(Math.round(reviewSummary.averageRating))}{"☆".repeat(5 - Math.round(reviewSummary.averageRating))}
              {" "}{reviewSummary.averageRating.toFixed(1)} ({reviewSummary.totalReviews})
            </span>
          )}
        </h2>

        {/* Write a review */}
          <div className="review-form-card">
            <h3>{t("farmBaskets.write_review", { defaultValue: "Write a Review" })}</h3>
            <div className="review-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`review-star-btn ${star <= newReview.rating ? "active" : "inactive"}`}
                  onClick={() => setNewReview((p) => ({ ...p, rating: star }))}
                  aria-label={t("farmBaskets.rate_stars", { defaultValue: "Rate {{count}} stars", count: star })}
                >
                  ★
                </button>
              ))}
            </div>
            <textarea
              className="review-textarea"
              value={newReview.comment}
              onChange={(e) => setNewReview((p) => ({ ...p, comment: e.target.value }))}
              placeholder={t("farmBaskets.review_placeholder", { defaultValue: "Share your experience with this farm..." })}
              maxLength={2000}
              rows={3}
            />
            <button
              type="button"
              className="review-submit-btn"
              onClick={handleSubmitReview}
              disabled={submittingReview || !newReview.comment.trim()}
            >
              {submittingReview ? t("farms.reviews.submitting", { defaultValue: "Submitting..." }) : t("farms.reviews.submit", { defaultValue: "Submit Review" })}
            </button>
          </div>

        {/* Reviews list */}
        {reviews.length === 0 ? (
          <p className="reviews-empty">
            {t("farmBaskets.no_reviews", { defaultValue: "No reviews yet. Be the first to share your experience!" })}
          </p>
        ) : (
          <div className="reviews-list">
            {reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-card-header">
                  <div className="review-author">
                    <div className="review-avatar">
                      {(review.authorName || "A")[0].toUpperCase()}
                    </div>
                    <span className="review-author-name">{review.authorName || t("farmBaskets.anonymous", { defaultValue: "Anonymous" })}</span>
                  </div>
                  <span className="review-date">
                    {new Date(review.createdUtc).toLocaleDateString()}
                  </span>
                </div>
                <div className="review-rating">
                  {"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}
                </div>
                <p className="review-comment">{review.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedBasket && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              &times;
            </button>
            <img
              src={selectedBasket.image}
              alt={selectedBasket.name}
              className="modal-image"
              loading="lazy"
            />
            <h2>{selectedBasket.name}</h2>
            <p>{selectedBasket.description}</p>
            <p className="price">{formatLek(selectedBasket.price)}</p>
            {selectedBasket.isHot && <span className="badge hot">{t("farmBaskets.badge_hot", { defaultValue: "Hot!" })}</span>}
            {selectedBasket.isNew && <span className="badge new">{t("farmBaskets.badge_new", { defaultValue: "New!" })}</span>}
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                className="add-to-cart-btn"
                onClick={() => handleAddToCart(selectedBasket)}
              >
                {t("farmBaskets.add_to_cart")}
              </button>
              <button
                className="add-to-cart-btn"
                style={{ background: "transparent", color: "var(--color-accent-primary)", border: "1px solid var(--color-border-subtle)" }}
                onClick={() => { closeModal(); navigate(`/baskets/${selectedBasket.id}`); }}
              >
                {t("baskets.view_details", { defaultValue: "Full Details" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmBaskets;
